import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/admin/_common/ui/popover";
import { Input } from "@/app/admin/_common/ui/input";
import { Checkbox } from "@/app/admin/_common/ui/checkbox";
import { ChevronDown, Search } from "lucide-react";
import ShowService from "@/app/_services/ShowService";
import { dayjsTz } from "@/app/_utils/dayjs";

const FALLBACK_IMG =
  "https://static.vecteezy.com/system/resources/previews/012/942/784/large_2x/broken-image-icon-isolated-on-a-white-background-no-image-symbol-for-web-and-mobile-apps-free-vector.jpg";

export type ShowOption = {
  id: string;
  title: string;
  variant_name?: string;
  start?: string;
  img_url?: string;
};

function ShowRow({
  show,
  checked,
  onToggle,
}: {
  show: ShowOption;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-[12.5px] hover:bg-muted">
      <Checkbox checked={checked} onCheckedChange={onToggle} />
      <img
        src={show.img_url || FALLBACK_IMG}
        className="size-[26px] flex-none rounded-[3px] object-cover"
        alt=""
      />
      <div className="flex min-w-0 flex-col">
        <span className="truncate">{show.title}</span>
        <span className="truncate text-[10.5px] text-muted-foreground">
          {[show.variant_name, show.start ? dayjsTz(show.start).format("MMM D, YYYY") : null]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
    </label>
  );
}

export default function ShowFilter({
  selectedShowIds,
  onChange,
}: {
  selectedShowIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [shows, setShows] = useState<ShowOption[]>([]);
  const [knownShows, setKnownShows] = useState<Map<string, ShowOption>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoading(true);
    const timer = setTimeout(() => {
      const request = search ? ShowService.getShow({ q: search }) : ShowService.getShow({ future: true });
      request
        .then((res: any) => {
          if (cancelled) return;
          const list = Array.isArray(res) ? res : [];
          const options: ShowOption[] = list.map((s: any) => ({
            id: s.id,
            title: s.title,
            variant_name: s.variant_name,
            start: s.start,
            img_url: s.img_url,
          }));
          setShows(options);
          setKnownShows((prev) => {
            const next = new Map(prev);
            options.forEach((s) => next.set(s.id, s));
            return next;
          });
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, search]);

  function toggleShow(id: string) {
    if (selectedShowIds.includes(id)) {
      onChange(selectedShowIds.filter((s) => s !== id));
    } else {
      onChange([...selectedShowIds, id]);
    }
  }

  const label =
    selectedShowIds.length === 0 ? "All shows" : `${selectedShowIds.length} show${selectedShowIds.length > 1 ? "s" : ""}`;

  const selectedShows = selectedShowIds
    .map((id) => knownShows.get(id))
    .filter((s): s is ShowOption => Boolean(s));
  const otherShows = shows.filter((s) => !selectedShowIds.includes(s.id));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-[30px] items-center gap-1.5 rounded-md border border-input bg-white px-3 text-[12.5px] text-foreground hover:bg-muted"
        >
          <span className="text-muted-foreground">Show:</span>
          {label}
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2">
        <div className="relative mb-2">
          <div className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center">
            <Search className="size-3.5 text-muted-foreground" />
          </div>
          <Input
            type="search"
            placeholder="Search shows…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-[30px] pl-8 text-[12.5px]"
            aria-label="Search shows"
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {selectedShows.length > 0 && (
            <>
              {selectedShows.map((show) => (
                <ShowRow key={show.id} show={show} checked onToggle={() => toggleShow(show.id)} />
              ))}
              <div className="my-1 border-t border-border" />
            </>
          )}
          {isLoading ? (
            <div className="px-2 py-3 text-center text-[12px] text-muted-foreground">Loading…</div>
          ) : otherShows.length === 0 && selectedShows.length === 0 ? (
            <div className="px-2 py-3 text-center text-[12px] text-muted-foreground">No shows found</div>
          ) : (
            otherShows.map((show) => (
              <ShowRow key={show.id} show={show} checked={false} onToggle={() => toggleShow(show.id)} />
            ))
          )}
        </div>
        {selectedShowIds.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="mt-2 w-full rounded-sm px-2 py-1 text-left text-[12px] text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            Clear selection
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
